<template>
  <div
    v-if="renderBanner && bannerMessages[desktopTextIndex]"
    ref="promotionalBanner"
    :class="{
      hidden: !displayPromotionalBannerOnPage && !isEmergency,
      'hidden md:block': isSearchBarActive && displayPromotionalBannerOnPage,
    }"
  >
    <ClientOnly>
      <Transition
        appear
        :appear-active-class="$style.bannerAppearActive"
        :appear-from-class="$style.bannerAppearFrom"
        :appear-to-class="$style.bannerAppearTo"
      >
        <aside
          class="flex h-40 w-full items-stretch justify-between overflow-x-hidden relative"
          :class="{
            'bg-surface-default-low dark:bg-static-brand-hi dark:mood-main text-static-default-hi mood-inverse':
              variant === 'promotional',
            'bg-static-danger-hi text-action-default-hi':
              variant === 'emergency',
          }"
          data-test="promotional-banner"
        >
          <div
            ref="marqueeBoundary"
            data-test="marquee-boundary"
            class="flex-1 overflow-hidden relative"
            :class="{ 'pl-64': marqueeTextNeedsAnimation && isMobile }"
          >
            <a
              v-if="
                bannerMessages[desktopTextIndex] &&
                !isMobile &&
                desktopDisplayText
              "
              :class="$style.stretchedLink"
              :href="bannerMessages[desktopTextIndex]?.uri"
              data-test="promotional-banner-desktop-link-sr-only"
              @click="onBannerClick(bannerMessages[desktopTextIndex])"
              @mouseover.passive="onDesktopMouseOver"
              @mouseout.passive="restartMessageInterval"
              @keyup.passive="onDesktopMouseOver"
              @focusin.passive="onDesktopMouseOver"
              @focusout.passive="restartMessageInterval"
            >
              <span class="sr-only">
                {{
                  documentToPlainTextString(
                    desktopDisplayText.richText as RichTextType,
                  )
                }}
              </span>
            </a>

            <div
              v-if="isMobile"
              ref="animationBoundary"
              class="relative h-full items-center"
              :class="[
                marqueeTextNeedsAnimation
                  ? `relative flex w-max gap-64 ${$style.marquee}`
                  : 'flex justify-center',
              ]"
              role="presentation"
              @click.passive="changeAnimationState('paused')"
              @focusin.passive="changeAnimationState('paused')"
              @focusout.passive="changeAnimationState('running')"
              @keyup.passive="changeAnimationState('running')"
              @mouseout.passive="changeAnimationState('running')"
              @mouseover.passive="changeAnimationState('paused')"
            >
              <div ref="textBoundary" class="flex whitespace-nowrap">
                <component
                  :is="bannerMessages[index] ? 'a' : 'div'"
                  v-for="(message, index) in bannerMessages"
                  :key="message.id"
                  :href="message.uri || undefined"
                  :class="[index > 0 ? 'ml-64' : '']"
                  @click="onBannerClick(message)"
                >
                  <RichText
                    :key="message.id"
                    :node-renderers-overrides="noHeadingsOrEmptyParagraphs"
                    class="body-1"
                    :rich-text="message.richText"
                    style-variant="none"
                  />
                </component>
              </div>

              <component
                :is="bannerMessages[index] ? 'a' : 'div'"
                v-for="(message, index) in bannerMessages"
                :key="message.id"
                :href="message.uri || undefined"
                @click="onBannerClick(message)"
              >
                <RichText
                  :key="message.id"
                  class="body-1"
                  :class="[marqueeTextNeedsAnimation ? 'block' : 'hidden']"
                  :node-renderers-overrides="noHeadingsOrEmptyParagraphs"
                  :rich-text="message.richText"
                  style-variant="none"
                />
              </component>
            </div>

            <div
              v-else
              class="relative flex items-center m-auto max-w-[1120px] h-full"
              :class="
                hasMultipleMessages ? 'justify-between' : 'justify-center'
              "
              role="presentation"
              @mouseover.passive="onDesktopMouseOver"
              @mouseout.passive="restartMessageInterval"
              @keyup.passive="onDesktopMouseOver"
              @focusin.passive="onDesktopMouseOver"
              @focusout.passive="restartMessageInterval"
            >
              <RevButtonIcon
                v-if="hasMultipleMessages"
                :aria-label="i18n(translations.alternativePrevious)"
                variant="ghost"
                class="ml-24 mr-12 lg:ml-0 z-10 !size-32"
                :icon="IconChevronLeft"
                data-test="button-previous-message"
                @click="goToPreviousText"
              />

              <Transition
                appear
                mode="out-in"
                :enter-active-class="$style.messageEnterActive"
                :enter-from-class="$style.messageEnterFrom"
                :enter-to-class="$style.messageEnterTo"
                :leave-active-class="$style.messageLeaveActive"
                :leave-from-class="$style.messageLeaveFrom"
                :leave-to-class="$style.messageLeaveTo"
              >
                <RichText
                  v-if="desktopDisplayText"
                  :key="desktopDisplayText.id"
                  data-test="promotional-banner-desktop-message"
                  class="body-1 h-full items-center flex justify-center"
                  :node-renderers-overrides="noHeadingsOrEmptyParagraphs"
                  :rich-text="desktopDisplayText.richText"
                  style-variant="none"
                />
              </Transition>

              <RevButtonIcon
                v-if="hasMultipleMessages"
                :aria-label="i18n(translations.alternativeNext)"
                variant="ghost"
                class="mr-24 ml-12 lg:mr-0 z-10 !size-32"
                :icon="IconChevronRight"
                data-test="button-next-message"
                @click="goToNextText"
              />
            </div>
          </div>

          <div
            class="absolute h-full top-0 left-1/2 -translate-x-1/2 -ml-20 min-w-300 pointer-events-none"
            :class="[
              $style.shimmer,
              {
                [$style.animateShimmer]: readyToShimmer && !hasShimmeredOnce,
              },
            ]"
            :style="`width: ${marqueeTextWidth}px`"
            @animationend="hasShimmeredOnce = true"
          />
        </aside>
      </Transition>

      <template v-if="bannerMessages.length" #fallback>
        <div
          class="h-40 w-full bg-action-default-min"
          data-test="promotional-banner-skeleton"
        />
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { useRoute } from '#imports'
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

import type { _ApiBlock } from '@backmarket/http-api/src/api-specs-content/models/block'
import type { RichText as RichTextType } from '@backmarket/http-api/src/api-specs-content/models/rich-text'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useClientOnlyComputedBreakpoint } from '@backmarket/utils/composables/useClientOnlyComputedBreakpoint'
import { Breakpoint } from '@backmarket/utils/dom/getCurrentBreakpoint'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'
import { tw } from '@backmarket/utils/string/tw'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { INLINES } from '@contentful/rich-text-types'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconChevronLeft } from '@ds/icons/IconChevronLeft'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { useMutationObserver } from '@vueuse/core'
import type { NodeRendererMap } from 'contentful-rich-text-vue-renderer'

import RichText from '../../blocks/RichText/RichText.vue'
import {
  findRichTextLinks,
  isNodeHyperlink,
} from '../../blocks/RichText/RichTextVueParser.utils'
import translations from '../../common/CMS.translations'

import { useFetchPromotionalBanner } from './useFetchPromotionalBanner'

const { isSearchBarActive, displayPromotionalBannerOnPage } = defineProps<{
  isSearchBarActive: boolean
  displayPromotionalBannerOnPage: boolean
}>()

const experiments = useExperiments()
const route = useRoute()
const { trackClick } = useTracking()
const breakpoint = useClientOnlyComputedBreakpoint()
const i18n = useI18n()

// pixels per second
const MARQUEE_SCROLL_SPEED = 50

const MESSAGE_INTERVAL_MS = 7_000

const { data } = useFetchPromotionalBanner(displayPromotionalBannerOnPage)

const marqueeBoundary = useTemplateRef<HTMLDivElement>('marqueeBoundary')
const textBoundary = useTemplateRef<HTMLDivElement>('textBoundary')
const animationBoundary = useTemplateRef<HTMLDivElement>('animationBoundary')
const banner = useTemplateRef<HTMLDivElement>('promotionalBanner')
const marqueeTextNeedsAnimation = ref<boolean>(false)
const marqueeScrollDuration = ref<number>()
const readyToShimmer = ref(false)
const hasShimmeredOnce = ref(false)
const marqueeTextWidth = ref(0)

const isMobile = ref(false)
const messageInterval = ref<ReturnType<typeof setInterval> | null>(null)

const desktopTextIndex = ref<number>(0)

const transitionDirection = ref<'next' | 'prev'>('next')
const enterFromSign = computed(() =>
  transitionDirection.value === 'next' ? -1 : 1,
)
const leaveToSign = computed(() =>
  transitionDirection.value === 'next' ? 1 : -1,
)

const variant = computed(() => {
  if (!data?.value) return 'promotional'

  const { blockList3: emergencyText } = data.value

  return emergencyText?.length ? 'emergency' : 'promotional'
})

const isEmergency = computed(() => variant.value === 'emergency')

// promotional banners are to be shown only on certain pages
// emergency banners can be shown everywhere
const renderBanner = computed(() => {
  if (isEmergency.value) return true

  return variant.value === 'promotional'
})

const textType = computed<'control' | 'variant' | 'emergency' | undefined>(
  () => {
    if (!data?.value) return undefined

    /**
     * Cases:
     * - we have emergency text -> it takes priority, return it
     * - we DO NOT have control text -> hide banner
     * - we have control & variant text, experiment or boolean are OFF -> display control banner
     * - we have control & NO variant text, experiment or boolean are OFF -> display control banner
     * - we have control & NO variant text, experiment AND boolean are ON -> hide banner (ie: B test is with the banner hidden)
     * - we have control & variant text, experiment AND boolean are ON -> display variant OR control according to experiment
     * - we have control text -> display control banner
     */

    const {
      blockList1: controlText,
      blockList2: variantText,
      blockList3: emergencyText,
      testEnabled,
    } = data.value

    const hasEmergencyText = emergencyText?.length
    if (hasEmergencyText) return 'emergency'

    const hasControlText = controlText?.length
    if (!hasControlText) return undefined

    const hasVariantText = variantText?.length
    const variantExperimentEnabled =
      testEnabled &&
      experiments['experiment.cmsPromotionalBanner'] === 'variantText'

    if (variantExperimentEnabled && !hasVariantText) return undefined
    if (variantExperimentEnabled && hasVariantText) return 'variant'

    return 'control'
  },
)

type BannerMessage = {
  originalIndex: number
  fullTextContent: string
  uri?: string
  richText: RichTextType
  id: string
}
// The V1 of this feature accepts a list of RichText elements but will only have 1,
// in the future it will be a "carousel" type of message display.
const bannerRawMessages = computed<
  Array<{ block: _ApiBlock<'RichText'>; originalIndex: number }>
>(() => {
  if (!data?.value || !textType.value) return []

  const {
    blockList1: controlText,
    blockList2: variantText,
    blockList3: emergencyText,
  } = data.value

  const isBetween1And3 = (index: number): index is 1 | 2 | 3 =>
    index >= 1 && index <= 3
  function isMessageDisplayedOnPage({
    index,
    messageTextType,
    routeName,
  }: {
    index: number
    messageTextType: 'control' | 'variant'
    routeName: string
  }) {
    const blockPosition = index + 1

    if (!isBetween1And3(blockPosition)) return true

    const property: `${'control' | 'variant'}Text${1 | 2 | 3}HideOnPages` = `${messageTextType}Text${blockPosition}HideOnPages`
    const hideOnPages = data.value?.[property] || []
    if (hideOnPages?.includes(routeName)) {
      return false
    }
    return true
  }

  switch (textType.value) {
    case 'control':
      // eslint-disable-next-line no-case-declarations
      const displayedControlText = controlText
        ?.map((block, index) => ({ block, originalIndex: index }))
        .filter(({ originalIndex }) => {
          return isMessageDisplayedOnPage({
            index: originalIndex,
            messageTextType: 'control',
            routeName: route.name?.toString() ?? '',
          })
        })
      return displayedControlText || []
    case 'variant':
      // eslint-disable-next-line no-case-declarations
      const displayedVariantText = variantText
        ?.map((block, index) => ({ block, originalIndex: index }))
        .filter(({ originalIndex }) => {
          return isMessageDisplayedOnPage({
            index: originalIndex,
            messageTextType: 'variant',
            routeName: route.name?.toString() ?? '',
          })
        })
      return displayedVariantText || []
    case 'emergency':
      return (
        emergencyText?.map((block, index) => ({
          block,
          originalIndex: index,
        })) || []
      )
    default:
      return []
  }
})

const bannerMessages = computed<BannerMessage[]>(() => {
  const messages: BannerMessage[] = []

  bannerRawMessages.value.forEach(({ block, originalIndex }) => {
    const links = findRichTextLinks(block.props.richText?.content)

    if (links?.length > 1) {
      return
    }

    const fullTextContent = documentToPlainTextString(
      block.props.richText as RichTextType,
    )

    messages.push({
      fullTextContent,
      uri: links?.[0]?.uri,
      richText: block.props.richText as RichTextType,
      id: block.id,
      originalIndex,
    })
  })

  return messages
})

const hasMultipleMessages = computed(() => bannerMessages.value.length > 1)

const desktopDisplayText = computed(() => {
  return bannerMessages.value?.[desktopTextIndex.value]
})

const trackingInfo = computed(() => {
  const info = {
    sourceUrl: route.fullPath,
    pageType: route.name?.toString(),
    pageName: route.params?.pageName?.toString(),
    zone:
      variant.value === 'promotional' ? 'promotionalBanner' : 'emergencyBanner',
    textType: textType.value ?? '',
  }

  return removeEmptyValuesInObject(info)
})

function onBannerClick(
  link:
    | { uri?: string; fullTextContent: string; originalIndex: number }
    | undefined,
) {
  if (!link) return

  trackClick({
    ...trackingInfo.value,
    action: 'bannerTextLinkClick',
    url: link.uri,
    name: link.fullTextContent,
    position: link.originalIndex + 1,
  })
}

// don't render any H tags by replacing them with <p> & hide empty paragraphs on all
const noHeadingsOrEmptyParagraphs: NodeRendererMap = {
  'heading-1': (node, key, next) => {
    return h(
      'p',
      { key, class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  'heading-2': (node, key, next) => {
    return h(
      'p',
      { class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  'heading-3': (node, key, next) => {
    return h(
      'p',
      { class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  'heading-4': (node, key, next) => {
    return h(
      'p',
      { class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  'heading-5': (node, key, next) => {
    return h(
      'p',
      { class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  'heading-6': (node, key, next) => {
    return h(
      'p',
      { class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  paragraph: (node, key, next) => {
    return h(
      'p',
      { key, class: tw`empty:hidden line-clamp-1` },
      next(node.content, key, next),
    )
  },
  [INLINES.HYPERLINK]: (node, key, next) => {
    if (!isNodeHyperlink(node)) return next(node.content, key, next)

    return h('span', { class: tw`body-1-link` }, next(node.content, key, next))
  },
}

async function initMarquee() {
  if (!marqueeBoundary.value || !textBoundary.value) return

  await nextTick()

  const { clientWidth: boundaryWidth } = marqueeBoundary.value
  const { clientWidth: textWidth } = textBoundary.value

  marqueeTextWidth.value = textWidth

  marqueeScrollDuration.value = textWidth / MARQUEE_SCROLL_SPEED
  marqueeTextNeedsAnimation.value = textWidth > boundaryWidth
}

watch([marqueeBoundary, textBoundary, bannerMessages], () => {
  if (!import.meta.client) return

  void initMarquee()
})

function changeAnimationState(state: 'running' | 'paused') {
  if (!animationBoundary.value) return

  animationBoundary.value.style.animationPlayState = state
}

const handleAnimationScrollState = () => changeAnimationState('running')

onMounted(() => {
  void initMarquee()

  window.addEventListener('resize', initMarquee, { passive: true })

  window.addEventListener('scroll', handleAnimationScrollState, {
    passive: true,
  })

  readyToShimmer.value = true

  restartMessageInterval()
})

function goToNextText() {
  transitionDirection.value = 'next'

  const { length } = bannerMessages.value
  if (!length) return

  desktopTextIndex.value = (desktopTextIndex.value + 1) % length

  restartMessageInterval()
}

function goToPreviousText() {
  transitionDirection.value = 'prev'

  const { length } = bannerMessages.value
  if (!length) return

  desktopTextIndex.value = (desktopTextIndex.value - 1 + length) % length

  restartMessageInterval()
}

function restartMessageInterval() {
  if (messageInterval.value) clearInterval(messageInterval.value)

  messageInterval.value = setInterval(() => {
    const { length } = bannerMessages.value
    if (!length) return

    goToNextText()
  }, MESSAGE_INTERVAL_MS)
}

function onDesktopMouseOver() {
  if (messageInterval.value) clearInterval(messageInterval.value)
}

onBeforeUnmount(() => {
  window.removeEventListener('resize', initMarquee)
  window.removeEventListener('scroll', handleAnimationScrollState)

  if (messageInterval.value) clearInterval(messageInterval.value)
})

useMutationObserver(
  banner,
  () => {
    if (!import.meta.client) return

    void initMarquee()
  },
  { attributeFilter: ['class'] },
)

watch(
  breakpoint,
  (newBreakpoint) => {
    // avoids the default SSR value for the breakpoint
    if (!import.meta.client) return

    isMobile.value = newBreakpoint < Breakpoint.MD
  },
  { immediate: true },
)
</script>

<style module>
.bannerAppearActive {
  transition: all 300ms cubic-bezier(0, 0, 0.4, 0);
}

.bannerAppearFrom {
  opacity: 0;
  transform: translateY(-2.5rem);
}

.bannerAppearTo {
  opacity: 1;
  transform: translateY(0);
}

.messageEnterActive,
.messageLeaveActive {
  transition: all 350ms cubic-bezier(0.4, 0, 0.2, 1);
}

.messageEnterFrom {
  opacity: 0;
  transform: translateY(calc(v-bind(enterFromSign) * 0.5rem));
}

.messageEnterTo,
.messageLeaveFrom {
  opacity: 1;
  transform: translateY(0);
}

.messageLeaveTo {
  opacity: 0;
  transform: translateY(calc(v-bind(leaveToSign) * 0.5rem));
}

@media (prefers-reduced-motion: reduce) {
  .bannerAppearActive,
  .messageEnterActive,
  .messageLeaveActive {
    transition: none;
  }

  .bannerAppearFrom,
  .messageEnterFrom,
  .messageLeaveTo {
    opacity: 1;
    transform: none;
  }
}

.stretchedLink {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.stretchedLink::after {
  content: '';
  position: absolute;
  inset: 0;
}

.marquee {
  animation: marquee calc(v-bind(marqueeScrollDuration) * 1s) linear infinite;
}

@keyframes marquee {
  to {
    /* 32px offset is there for the 64px gap between each text
     -> avoids stuttering during transition */
    transform: translateX(calc(-50% - 32px));
  }
}

@keyframes animateShimmer {
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateX(30%);
  }
}

.shimmer {
  opacity: 0;
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    rgba(0, 0, 0, 0.8) 15%,
    rgba(0, 0, 0, 0.8) 85%,
    transparent 100%
  );

  background: linear-gradient(
    -20deg,
    transparent 35%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 85%
  );

  @media (prefers-color-scheme: dark) {
    background: linear-gradient(
      -20deg,
      transparent 35%,
      rgba(255, 255, 255, 0.7) 50%,
      transparent 85%
    );
  }
}

.animateShimmer {
  animation: animateShimmer 1600ms linear;

  @media (min-width: theme('screens.md')) {
    animation: animateShimmer 1800ms linear;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  animation-delay: 2s;
}
</style>
